/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer, Box, Typography, Toolbar, Divider } from '@mui/material';
import { RootState } from '@ink-ai/portal/reducers';
import { Compose } from '@ink-ai/portal/common/components/ai-assistant/Compose/Compose';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { AssistantTabs } from '@ink-ai/portal/common/components/ai-assistant/Tabs/AssistantTabs';
import {
  ASSISTANT_TABS,
  aiAssistant,
} from '@ink-ai/portal/reducers/ai-assistant';
import { ChatUI } from '@ink-ai/portal/taskpane/components/Chat/Chat';
import { CitationPage } from '@ink-ai/portal/common/components/ai-assistant/Citation/CitationPage';
import Inspect from '@ink-ai/portal/taskpane/components/inspect/Inspect';

interface PluginWindowProps {
  open: boolean;
}

export const PluginWindow: React.FC<PluginWindowProps> = ({ open }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const drawerWidth = 350;
  const aiAssistantState = useSelector((state: RootState) => state.aiAssistant);

  useEffect(() => {
    dispatch(aiAssistant.actions.setCurrentTab(ASSISTANT_TABS.COMPOSE));
  }, [dispatch]);

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: (theme) =>
            theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          marginRight: open ? `${drawerWidth}px` : 0,
        }}
      ></Box>
      {open && (
        <Drawer
          variant="persistent"
          open={open}
          anchor="right"
          PaperProps={{
            style: { width: `${drawerWidth}px`, overflow: 'hidden' },
          }}
        >
          <Toolbar />
          <Box sx={{ display: 'flex', alignItems: 'center', padding: 2 }}>
            <FaceRetouchingNaturalIcon className="mr-2" />
            <Typography variant="h6" sx={{ color: 'inherit' }}>
              {t('plugin.aiAssistant')}
            </Typography>
          </Box>
          <div className="px-5 pb-2">
            <AssistantTabs
              tabs={{
                COMPOSE: true,
                CITATION: true,
                CHATBOT: true,
                INSPECT: true,
              }}
            />
          </div>
          <Divider />
          {aiAssistantState.currentTab === ASSISTANT_TABS.CITATION && (
            <CitationPage />
          )}
          {aiAssistantState.currentTab === ASSISTANT_TABS.COMPOSE && (
            <Compose />
          )}
          {aiAssistantState.currentTab === ASSISTANT_TABS.INSPECT && (
            <Inspect />
          )}
          {aiAssistantState.currentTab === ASSISTANT_TABS.CHATBOT && <ChatUI />}
        </Drawer>
      )}
    </Box>
  );
};
